/**
 * Coach Schedule — Full-page schedule view for coaches.
 */
import { useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ArrowLeft, Plus } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { FONT, RADIUS, SPACE } from '@/constants/tokens';
import CoachSchedule from '@/components/coach/CoachSchedule';
import ScheduleSessionModal from '@/components/coach/ScheduleSessionModal';
import { addSession, updateSession, type CoachSession } from '@/lib/coachSchedule';
import { listMyClientLinks, resolveProfileById, type TrainerClientLink } from '@/lib/trainers';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useCoachAccess } from '@/lib/coachAccess';

interface ClientOption {
  id: string;
  name: string;
}

export default function CoachScheduleScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  // Route guard: coach_pro (or org_pro) required.
  const { hasAccess, isLoading: accessLoading } = useCoachAccess();
  useEffect(() => {
    if (!accessLoading && !hasAccess) {
      router.replace('/(tabs)/home');
    }
  }, [accessLoading, hasAccess]);

  const [showModal, setShowModal] = useState(false);
  const [editSession, setEditSession] = useState<CoachSession | undefined>();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadClients = useCallback(async () => {
    try {
      const links = await listMyClientLinks();
      const active = links.filter((l: TrainerClientLink) => l.status === 'active');
      const profiles = await Promise.all(active.map((l: TrainerClientLink) => resolveProfileById(l.client_profile_id)));
      setClients(
        profiles.filter(Boolean).map((p) => ({
          id: p!.id,
          name: p!.display_name || p!.username || 'Unknown',
        })),
      );
    } catch {
      setClients([]);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const handleSave = (data: {
    client_id: string;
    client_name: string;
    session_date: string;
    start_time: string;
    duration_minutes: number;
    notes: string;
  }) => {
    if (!profile?.id) return;
    const sessionData = { ...data, coach_id: profile.id, status: 'scheduled' as const };
    if (editSession) {
      void updateSession(editSession.id, sessionData);
    } else {
      void addSession(sessionData);
    }
    setShowModal(false);
    setEditSession(undefined);
    setRefreshKey((k) => k + 1);
  };

  const handleEdit = (session: CoachSession) => {
    setEditSession(session);
    setShowModal(true);
  };

  // Guard: render nothing while access check is in progress or unauthorized
  if (accessLoading || !hasAccess) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <ActivityIndicator style={{ flex: 1 }} color={c.accent} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/coach-dashboard')} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>Schedule</Text>
        <View style={{ width: 30 }} />
      </View>

      {/* Calendar */}
      <View style={{ flex: 1, paddingHorizontal: SPACE['2'] }}>
        {profile?.id && (
          <CoachSchedule
            coachId={profile.id}
            onEditSession={handleEdit}
            refreshKey={refreshKey}
          />
        )}
      </View>

      {/* Floating Add Button */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: c.accent, bottom: Math.max(32, insets.bottom + 16) }]}
        onPress={() => { setEditSession(undefined); setShowModal(true); }}
        activeOpacity={0.8}
      >
        <Plus size={28} color={c.accentForeground} />
      </TouchableOpacity>

      {/* Schedule Modal */}
      <ScheduleSessionModal
        visible={showModal}
        clients={clients}
        editSession={editSession}
        onSave={handleSave}
        onCancel={() => { setShowModal(false); setEditSession(undefined); }}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  backBtn: { padding: 4 },
  headerTitle: {
    flex: 1,
    fontSize: FONT.xl,
    fontFamily: TYPO.heading.bold,
  },
  fab: {
    position: 'absolute',
    bottom: 32,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
  },
});
